// Phase 2.3.A: User Analytics
// Track user behavior and product analytics using PostHog
// Phase 2: Academic Level Analytics Integration

import posthog from 'posthog-js';
import type { EducationLevel, LearningStyle } from './academicLevels';

// PostHog initialization status
let isInitialized = false;

/**
 * Initialize PostHog analytics
 * Should be called once on app startup
 */
export function initAnalytics(): void {
  if (isInitialized) {
    console.warn('[Analytics] Already initialized');
    return;
  }

  const posthogKey = import.meta.env.VITE_POSTHOG_KEY;
  const posthogHost = import.meta.env.VITE_POSTHOG_HOST || 'https://app.posthog.com';
  
  if (!posthogKey) {
    // Only warn in production - in dev it's expected to be optional
    if (import.meta.env.PROD) {
      console.warn('[Analytics] PostHog key not configured - analytics disabled');
    }
    return;
  }

  try {
    posthog.init(posthogKey, {
      api_host: posthogHost,
      autocapture: false, // Disable automatic event capture for privacy
      capture_pageview: true,
      capture_pageleave: true,
      disable_session_recording: import.meta.env.DEV, // Disable in dev
      loaded: (posthog) => {
        if (import.meta.env.DEV) {
          posthog.opt_out_capturing(); // Opt-out in development
          console.log('[Analytics] PostHog initialized (dev mode - opted out)');
        } else {
          console.log('[Analytics] PostHog initialized');
        }
      },
    });
    
    isInitialized = true;
  } catch (error) {
    console.error('[Analytics] Failed to initialize PostHog:', error);
  }
}

/**
 * Track a custom event
 */
export function trackEvent(event: string, properties?: Record<string, any>): void {
  if (!isInitialized) {
    // Silent fail if analytics not configured - it's optional
    return;
  }

  try {
    posthog.capture(event, {
      ...properties,
      timestamp: new Date().toISOString(),
    });
    
    if (import.meta.env.DEV) {
      console.log('[Analytics] Event tracked:', event, properties);
    }
  } catch (error) {
    console.error('[Analytics] Failed to track event:', event, error);
  }
}

/**
 * Identify a user
 */
export function identifyUser(userId: string, properties?: Record<string, any>): void {
  if (!isInitialized) {
    // Silent fail if analytics not configured - it's optional
    return;
  }

  try {
    posthog.identify(userId, properties);
    
    if (import.meta.env.DEV) {
      console.log('[Analytics] User identified:', userId, properties);
    }
  } catch (error) {
    console.error('[Analytics] Failed to identify user:', error);
  }
}

/**
 * Reset user identity (on logout)
 */
export function resetUser(): void {
  if (!isInitialized) {
    return;
  }

  try {
    posthog.reset();
    
    if (import.meta.env.DEV) {
      console.log('[Analytics] User reset');
    }
  } catch (error) {
    console.error('[Analytics] Failed to reset user:', error);
  }
}

/**
 * Track page view
 */
export function trackPageView(path: string, properties?: Record<string, any>): void {
  if (!isInitialized) {
    return;
  }

  try {
    posthog.capture('$pageview', {
      $current_url: window.location.href,
      path,
      ...properties,
    });
    
    if (import.meta.env.DEV) {
      console.log('[Analytics] Page view tracked:', path);
    }
  } catch (error) {
    console.error('[Analytics] Failed to track page view:', error);
  }
}

// ============================================================================
// Pre-defined Analytics Events
// ============================================================================

export const AnalyticsEvents = {
  // Authentication Events
  USER_SIGNUP: 'user_signup',
  USER_LOGIN: 'user_login',
  USER_LOGOUT: 'user_logout',
  
  // PDF Upload Events
  PDF_UPLOAD_START: 'pdf_upload_start',
  PDF_UPLOAD_SUCCESS: 'pdf_upload_success',
  PDF_UPLOAD_FAILED: 'pdf_upload_failed',
  PDF_PROCESSING_START: 'pdf_processing_start',
  PDF_PROCESSING_SUCCESS: 'pdf_processing_success',
  PDF_PROCESSING_FAILED: 'pdf_processing_failed',
  
  // Chat Events
  CHAT_MESSAGE_SENT: 'chat_message_sent',
  CHAT_RESPONSE_RECEIVED: 'chat_response_received',
  CHAT_ERROR: 'chat_error',
  CHAT_CONVERSATION_START: 'chat_conversation_start',
  
  // Quiz Events
  QUIZ_START: 'quiz_start',
  QUIZ_ANSWER: 'quiz_answer',
  QUIZ_COMPLETE: 'quiz_complete',
  QUIZ_SCORE: 'quiz_score',
  
  // Notes Events
  NOTE_VIEW: 'note_view',
  NOTE_CREATE: 'note_create',
  NOTE_EDIT: 'note_edit',
  NOTE_DELETE: 'note_delete',
  GOLDEN_NOTE_VIEW: 'golden_note_view',
  
  // Course Events
  COURSE_CREATE: 'course_create',
  COURSE_VIEW: 'course_view',
  COURSE_EDIT: 'course_edit',
  COURSE_DELETE: 'course_delete',
  
  // Feature Usage
  FEATURE_USED: 'feature_used',
  SETTINGS_CHANGED: 'settings_changed',
  HELP_VIEWED: 'help_viewed',
  
  // Errors
  ERROR_OCCURRED: 'error_occurred',
  API_ERROR: 'api_error',
  VALIDATION_ERROR: 'validation_error',
  
  // Onboarding Events
  ONBOARDING_STARTED: 'onboarding_started',
  ONBOARDING_STEP_COMPLETED: 'onboarding_step_completed',
  ONBOARDING_COMPLETED: 'onboarding_completed',
  
  // Education Level Events (Phase 2)
  EDUCATION_LEVEL_SELECTED: 'education_level_selected',
  EDUCATION_LEVEL_CHANGED: 'education_level_changed',
  ACADEMIC_PROFILE_COMPLETED: 'academic_profile_completed',
  LEARNING_STYLE_SELECTED: 'learning_style_selected',
  
  // PhD Research Hub Events (Phase 2)
  RESEARCH_PROJECT_CREATED: 'research_project_created',
  RESEARCH_PROJECT_UPDATED: 'research_project_updated',
  RESEARCH_PROJECT_DELETED: 'research_project_deleted',
  RESEARCH_STAGE_CHANGED: 'research_stage_changed',
  RESEARCH_MILESTONE_COMPLETED: 'research_milestone_completed',
  
  // Citation Events (Phase 2)
  CITATION_ADDED: 'citation_added',
  CITATION_DOI_LOOKUP: 'citation_doi_lookup',
  CITATION_DOI_SUCCESS: 'citation_doi_success',
  CITATION_DOI_FAILED: 'citation_doi_failed',
  CITATION_STYLE_CHANGED: 'citation_style_changed',
  CITATION_EXPORTED_BIBTEX: 'citation_exported_bibtex',
  CITATION_EXPORTED_CSV: 'citation_exported_csv',
  CITATION_COPIED: 'citation_copied',
  CITATION_TAG_ADDED: 'citation_tag_added',
  CITATION_SEARCH_PERFORMED: 'citation_search_performed',
  
  // Feature Access Events (Phase 2)
  FEATURE_ACCESSED: 'feature_accessed',
  FEATURE_BLOCKED: 'feature_blocked',
} as const;

// ============================================================================
// Convenience Functions for Common Events
// ============================================================================

export function trackUserSignup(userId: string, method: 'email' | 'google' | 'github'): void {
  identifyUser(userId, { signup_method: method });
  trackEvent(AnalyticsEvents.USER_SIGNUP, { method });
}

export function trackUserLogin(userId: string, method: 'email' | 'google' | 'github'): void {
  identifyUser(userId);
  trackEvent(AnalyticsEvents.USER_LOGIN, { method });
}

export function trackUserLogout(): void {
  trackEvent(AnalyticsEvents.USER_LOGOUT);
  resetUser();
}

export function trackPDFUpload(fileName: string, fileSize: number, weekNumber: string): void {
  trackEvent(AnalyticsEvents.PDF_UPLOAD_START, {
    file_name: fileName,
    file_size_mb: (fileSize / 1024 / 1024).toFixed(2),
    week_number: weekNumber,
  });
}

export function trackPDFProcessing(
  success: boolean,
  duration: number,
  notesCount?: number,
  mcqCount?: number,
  openQuestionsCount?: number
): void {
  const event = success ? AnalyticsEvents.PDF_PROCESSING_SUCCESS : AnalyticsEvents.PDF_PROCESSING_FAILED;
  
  trackEvent(event, {
    duration_seconds: (duration / 1000).toFixed(2),
    golden_notes_count: notesCount,
    mcq_count: mcqCount,
    open_questions_count: openQuestionsCount,
  });
}

export function trackChatMessage(messageLength: number, conversationId?: string): void {
  trackEvent(AnalyticsEvents.CHAT_MESSAGE_SENT, {
    message_length: messageLength,
    conversation_id: conversationId,
  });
}

export function trackChatResponse(responseLength: number, duration: number): void {
  trackEvent(AnalyticsEvents.CHAT_RESPONSE_RECEIVED, {
    response_length: responseLength,
    duration_seconds: (duration / 1000).toFixed(2),
  });
}

export function trackQuizComplete(score: number, totalQuestions: number, duration: number): void {
  trackEvent(AnalyticsEvents.QUIZ_COMPLETE, {
    score,
    total_questions: totalQuestions,
    percentage: ((score / totalQuestions) * 100).toFixed(1),
    duration_seconds: (duration / 1000).toFixed(2),
  });
}

export function trackError(errorType: string, errorMessage: string, context?: Record<string, any>): void {
  trackEvent(AnalyticsEvents.ERROR_OCCURRED, {
    error_type: errorType,
    error_message: errorMessage,
    ...context,
  });
}

// ============================================================================
// Phase 2: Academic Level Analytics Functions
// ============================================================================

/**
 * Enrich event properties with user's academic context
 */
function enrichWithAcademicContext(properties?: Record<string, any>): Record<string, any> {
  const academicProfile = localStorage.getItem('academic_profile');
  
  if (!academicProfile) {
    return properties || {};
  }
  
  try {
    const profile = JSON.parse(academicProfile);
    return {
      ...properties,
      education_level: profile.educationLevel,
      field_of_study: profile.fieldOfStudy,
      learning_style: profile.learningStyle,
      institution: profile.institution,
    };
  } catch {
    return properties || {};
  }
}

/**
 * Track education level selection or change
 */
export function trackEducationLevelChange(
  level: EducationLevel,
  isInitial: boolean = false
): void {
  const event = isInitial 
    ? AnalyticsEvents.EDUCATION_LEVEL_SELECTED 
    : AnalyticsEvents.EDUCATION_LEVEL_CHANGED;
  
  trackEvent(event, {
    education_level: level,
    is_initial: isInitial,
    timestamp: new Date().toISOString(),
  });
}

/**
 * Track academic profile completion
 */
export function trackProfileCompletion(profile: {
  educationLevel: EducationLevel;
  fieldOfStudy: string;
  learningStyle?: LearningStyle;
  institution?: string;
  graduationYear?: string;
  gpa?: string;
  academicGoals?: string[];
}): void {
  // Calculate profile completeness
  const hasOptionalFields = [
    !!profile.institution,
    !!profile.graduationYear,
    !!profile.gpa,
    !!profile.learningStyle,
    !!profile.academicGoals?.length,
  ].filter(Boolean).length;
  
  trackEvent(AnalyticsEvents.ACADEMIC_PROFILE_COMPLETED, {
    education_level: profile.educationLevel,
    field_of_study: profile.fieldOfStudy,
    learning_style: profile.learningStyle,
    has_institution: !!profile.institution,
    has_graduation_year: !!profile.graduationYear,
    has_gpa: !!profile.gpa,
    goals_count: profile.academicGoals?.length || 0,
    profile_completeness: (hasOptionalFields / 5) * 100, // percentage
  });
  
  // Update user properties in PostHog
  if (isInitialized) {
    posthog.setPersonProperties({
      education_level: profile.educationLevel,
      field_of_study: profile.fieldOfStudy,
      learning_style: profile.learningStyle,
      institution: profile.institution,
    });
  }
}

/**
 * Track learning style selection
 */
export function trackLearningStyleSelection(style: LearningStyle): void {
  trackEvent(AnalyticsEvents.LEARNING_STYLE_SELECTED, {
    learning_style: style,
  });
}

/**
 * Track research project operations (PhD Hub)
 */
export function trackResearchProject(
  action: 'created' | 'updated' | 'deleted',
  details: {
    projectId: string;
    stage?: string;
    progress?: number;
    hasMilestones?: boolean;
    citationCount?: number;
  }
): void {
  const eventMap = {
    created: AnalyticsEvents.RESEARCH_PROJECT_CREATED,
    updated: AnalyticsEvents.RESEARCH_PROJECT_UPDATED,
    deleted: AnalyticsEvents.RESEARCH_PROJECT_DELETED,
  };
  
  trackEvent(eventMap[action], enrichWithAcademicContext({
    project_id: details.projectId,
    stage: details.stage,
    progress: details.progress,
    has_milestones: details.hasMilestones,
    citation_count: details.citationCount,
  }));
}

/**
 * Track research stage changes
 */
export function trackResearchStageChange(
  projectId: string,
  fromStage: string,
  toStage: string,
  progress: number
): void {
  trackEvent(AnalyticsEvents.RESEARCH_STAGE_CHANGED, enrichWithAcademicContext({
    project_id: projectId,
    from_stage: fromStage,
    to_stage: toStage,
    progress,
  }));
}

/**
 * Track research milestone completion
 */
export function trackMilestoneCompletion(
  projectId: string,
  milestoneTitle: string,
  completionPercentage: number
): void {
  trackEvent(AnalyticsEvents.RESEARCH_MILESTONE_COMPLETED, enrichWithAcademicContext({
    project_id: projectId,
    milestone_title: milestoneTitle,
    completion_percentage: completionPercentage,
  }));
}

/**
 * Track citation operations
 */
export interface CitationAnalytics {
  citationId?: string;
  citationType?: string;
  hasDOI?: boolean;
  style?: string;
  exportFormat?: string;
  tags?: string[];
}

export function trackCitation(
  action: 'added' | 'doi_lookup' | 'doi_success' | 'doi_failed' | 'style_changed' | 'exported_bibtex' | 'exported_csv' | 'copied' | 'tag_added' | 'search_performed',
  details: CitationAnalytics
): void {
  const eventMap = {
    added: AnalyticsEvents.CITATION_ADDED,
    doi_lookup: AnalyticsEvents.CITATION_DOI_LOOKUP,
    doi_success: AnalyticsEvents.CITATION_DOI_SUCCESS,
    doi_failed: AnalyticsEvents.CITATION_DOI_FAILED,
    style_changed: AnalyticsEvents.CITATION_STYLE_CHANGED,
    exported_bibtex: AnalyticsEvents.CITATION_EXPORTED_BIBTEX,
    exported_csv: AnalyticsEvents.CITATION_EXPORTED_CSV,
    copied: AnalyticsEvents.CITATION_COPIED,
    tag_added: AnalyticsEvents.CITATION_TAG_ADDED,
    search_performed: AnalyticsEvents.CITATION_SEARCH_PERFORMED,
  };
  
  trackEvent(eventMap[action], enrichWithAcademicContext({
    citation_id: details.citationId,
    citation_type: details.citationType,
    has_doi: details.hasDOI,
    style: details.style,
    export_format: details.exportFormat,
    tag_count: details.tags?.length,
  }));
}

/**
 * Track AI chat interactions with academic context
 */
export function trackChatInteractionWithContext(
  messageLength: number,
  hasFiles: boolean = false,
  fileCount: number = 0,
  conversationId?: string
): void {
  trackEvent(AnalyticsEvents.CHAT_MESSAGE_SENT, enrichWithAcademicContext({
    message_length: messageLength,
    has_files: hasFiles,
    file_count: fileCount,
    conversation_id: conversationId,
  }));
}

/**
 * Track feature access attempts (for feature gating)
 */
export function trackFeatureAccess(
  featureName: string,
  allowed: boolean,
  requiredLevel?: EducationLevel
): void {
  const event = allowed 
    ? AnalyticsEvents.FEATURE_ACCESSED 
    : AnalyticsEvents.FEATURE_BLOCKED;
  
  trackEvent(event, enrichWithAcademicContext({
    feature_name: featureName,
    allowed,
    required_level: requiredLevel,
  }));
}

// ============================================================================
// Analytics Summary Functions (for dashboards)
// ============================================================================

/**
 * Get education level distribution from PostHog
 * Note: This would typically be done server-side or via PostHog's API
 */
export interface AnalyticsSummary {
  educationLevelDistribution: Record<EducationLevel, number>;
  featureUsage: Record<string, { total: number; byLevel: Record<EducationLevel, number> }>;
  topCitationStyles: Array<{ style: string; count: number }>;
  doiLookupSuccessRate: number;
  avgSessionDuration: number;
  activeUsers: number;
}

/**
 * Example function to calculate DOI lookup success rate
 * In production, this would query PostHog's API or your analytics backend
 */
export function calculateDOISuccessRate(
  lookupAttempts: number,
  successfulLookups: number
): number {
  if (lookupAttempts === 0) return 0;
  return (successfulLookups / lookupAttempts) * 100;
}
